/**
 * One domain serves two SPAs (TML-5374, 3B-4 / FE-8 / 3B-3): which app owns a path, which token roles
 * open the admin app, and the notice one app hands the other across a full page load. The Shell
 * (`apps/front/web/src/Shell.tsx`) picks an app on every load, the student app's catch-all and the
 * admin app's landings ask the same questions — so the answers live here, once. No imports: the Shell
 * reads this module before either app, or `@graphql-v2`, has loaded.
 */

export type AppName = 'admin' | 'student';

/** Path prefixes the student app owns; everything else belongs to the admin app. */
const STUDENT_APP_PREFIXES = ['/student/', '/co/', '/register', '/verify', '/u/'];

/** Company-scoped e-mail links into admin surfaces, despite `/co/` — the AI-task run notification (TML-5500). */
const ADMIN_DEEP_LINK = /^\/co\/[^/]+\/ai-tasks(\/|$)/;

/** True for a path that opens the admin app whatever the token says. */
export function isAdminDeepLink(pathname: string): boolean {
  return ADMIN_DEEP_LINK.test(pathname);
}

/** True when `pathname` belongs to the student app — the other SPA, reached only with a full page load. */
export function isStudentAppPath(pathname: string): boolean {
  if (isAdminDeepLink(pathname)) return false;
  return STUDENT_APP_PREFIXES.some((prefix) => pathname === prefix.replace(/\/$/, '') || pathname.startsWith(prefix));
}

/**
 * FE-8: true when a token's `role` opens the admin app — any access level but Student, a level this
 * build does not know included; never a staff-only or a missing role. The role is only a hint: the
 * admin app re-reads the level from `myAccess`.
 */
export function isAdminAppRole(role: string | null | undefined): boolean {
  return !!role && role !== 'STUDENT' && role !== 'STAFF';
}

/**
 * The app a full page load opens (the Shell). The path decides; on an ambiguous one (`/`, an admin
 * URL) the token's role does — a Student's goes to the learner app; any other level, staff, or no
 * session at all (sign-in lives there) to the admin app.
 */
export function appForPath(pathname: string, claims: { role?: string | null } | null | undefined): AppName {
  if (isAdminDeepLink(pathname)) return 'admin';
  if (isStudentAppPath(pathname)) return 'student';
  return claims?.role === 'STUDENT' ? 'student' : 'admin';
}

/** 3B-3: a toast one app owes the person after sending them to the other, where its own toasts are gone. */
export interface CrossAppNotice {
  variant: 'warning' | 'info';
  message: string;
}

const NOTICE_KEY = 'tml.crossAppNotice';

/** Leaves `notice` for the app the next page load opens. Storage may be off (private mode) — then it is lost. */
export function stashCrossAppNotice(notice: CrossAppNotice): void {
  try {
    sessionStorage.setItem(NOTICE_KEY, JSON.stringify(notice));
  } catch {
    // Nothing to hand over without storage.
  }
}

/** The notice the other app left, once — reading it removes it. */
export function takeCrossAppNotice(): CrossAppNotice | null {
  try {
    const raw = sessionStorage.getItem(NOTICE_KEY);
    if (raw === null) return null;
    sessionStorage.removeItem(NOTICE_KEY);
    const notice = JSON.parse(raw) as Partial<CrossAppNotice> | null;
    const valid =
      (notice?.variant === 'warning' || notice?.variant === 'info') &&
      typeof notice.message === 'string' &&
      notice.message !== '';
    return valid ? (notice as CrossAppNotice) : null;
  } catch {
    return null;
  }
}
